import React, { useCallback, useMemo, useRef } from "react";
import { Button, InputNumber, message } from "antd";
import { SActionBtn, SDrawerOrModal, SLowcodeList, STableActions, STags } from "shj-components";
import {getConfig} from './config.tsx'
import {
  getGeneralPage,
  getProductManagementPage,
  standardWarningDetailsPage,
} from "@/services/product.ts";
import { getEnums, httpOrgList } from "@/services/homeService.ts";
import DebounceSearchSelect from "@/pages/baseProduct/components/DebounceSearchSelect";
import { apiData2Table } from "@/utils/data.ts";
import { isEmpty } from "lodash-es";
import { useNavigate } from "react-router-dom";
import { fileDownload } from "@/utils/download.ts";
import UrlConfig from "@/api/UrlConfigs.ts";
import { useSetState } from "ahooks";
import { METHODS } from "@/api";
const GeneralModal = (props) => {
  const navigate = useNavigate()
  const colorEnums = {
    NORMAL: {
      bgColor: "#F7F8FA",
      fontColor: '#575859',
      textColor: '#2D2D2E',
    },
    LOW_COST: {
      bgColor: "#D7F7EA",
      fontColor: '#00A865',
      textColor: '#00A865',
    },
    HIGH_COST: {
      bgColor: "#FFEAE8",
      fontColor: '#EB3723',
      textColor: '#EB3723',
    }
  }
  const {open, onClose} = props;
  const listRef = useRef();
  const [state, setState] = useSetState<any>({
    exportLoading: false,
    productId: '',
    generalId: '',
  })

  const fetchList = ({current, pageSize, query}) => {
    return new Promise((resolve, reject) => {
      delete query.costDifference
      delete query.costDifferenceRate
      standardWarningDetailsPage({
        data: {
          ...query,
          orderByAlertType: true,
        },
        params: {pageSize, pageId: current}
      }).then(res => {
        resolve(apiData2Table(res))
      }).catch(reject);
    })
  }

  const getOptions = async () => {
    const companyList = await httpOrgList({status: undefined})
    const alertTypeEnum = await getEnums('plus-product', 'com.shj.saas.plus.product.enums.StandardWarningAlertTypeEnum')
    return {companyList, alertTypeEnum}
  }

  const onViewConfig = (record = {}) => {
    let path = `/config/baseProduct/generalManage?tabKey=STANDARD`
    if(!isEmpty(record)){
      path += `&businessId=${record.businessId}`
    }
    window.open(path, '_blank')
  }

  const onExport = () => {
    const data = listRef.current?.getSearch()?.getFieldsValue()
    data.orderByAlertType = true;
    delete data.costDifference
    delete data.costDifferenceRate
    setState({ exportLoading: true })
    fileDownload(UrlConfig.PlusProductWeb.standardWarningDetailsExport, {method: METHODS.POST, data: data, error: false})
      .then(() => setState({ exportLoading: false }))
      .catch(async (err) => {
        if (err instanceof Blob || Object.prototype.toString.call(err) === '[object Blob]') {
          try {
            const text = await err.text();
            const jsonData = JSON.parse(text);
            message.error(jsonData.message);
          } catch (error) {
            return err;
          }
        }
        setState({ exportLoading: false })
      })
  }

  // 缓存 fixedParams
  const fixedParams = useMemo(() => ({
    prodTypes: ['PRODUCT_PACKAGE'],
    enabled: undefined,
  }), []);

  // 缓存 onChange 处理函数
  const handleProductChange = useCallback((form, name, v) => {
    form.setFieldValue(name, v);
    setState({ productId: v });
  }, []);

  // 缓存 query 对象
  const query = useMemo(() =>
      state.productId ? { id: state.productId } : undefined
    , [state.productId]);

  // 优化后的 DebounceSearchSelect 组件
  const MemoizedDebounceSearchSelect = React.memo(DebounceSearchSelect, (prevProps, nextProps) => {
    return (
      prevProps.disabled === nextProps.disabled &&
      prevProps.value === nextProps.value &&
      JSON.stringify(prevProps.query) === JSON.stringify(nextProps.query)
    );
  });

  // 优化后的 searchSelectRender 函数
  const searchSelectRender = useCallback(({ name, form, disabled }) => {
    return (
      <MemoizedDebounceSearchSelect
        allowClear
        label={'产品'}
        fetchOptions={getProductManagementPage}
        fixedParams={fixedParams}
        onChange={(v) => handleProductChange(form, name, v)}
        fieldNames={{ label: 'prodName' }}
        style={{ width: '100%' }}
        query={query}
        disabled={disabled}
      />
    );
  }, [fixedParams, handleProductChange, query]);

  const fixedGeneralParams = useMemo(() => ({
    enabled: undefined,
  }), []);

  // 缓存 onChange 处理函数
  const handleGeneralChange = useCallback((form, name, v) => {
    form.setFieldValue(name, v);
    setState({ generalId: v });
  }, []);

  const generalQuery = useMemo(() =>
      state.generalId ? { id: state.generalId } : undefined
    , [state.generalId]);

  const generalRender = useCallback(({ name, form, disabled }) => {
    return (
      <MemoizedDebounceSearchSelect
        allowClear
        label={'通项'}
        style={{ width: '100%' }}
        onChange={v => handleGeneralChange(form, name, v)}
        nameKey={'itemNameKey'}
        fixedParams={fixedGeneralParams}
        query={generalQuery}
        fetchOptions={getGeneralPage}
        fieldNames={{ label: 'itemName', value: 'id' }}
        disabled={disabled}
      />
    );
  }, [fixedGeneralParams, handleGeneralChange, generalQuery]);

  const extensions = {
    searchMode: 'group',
    request: {fetchList, getOptions},
    events: {onAdd: () => onViewConfig(), },
    customRender: {
      actionRender(_, record){
        return <STableActions>
          <SActionBtn onClick={() => onViewConfig(record)}>查看配置</SActionBtn>
        </STableActions>
      },
      searchSelectRender,
      generalRender,
      rangeRender: ({ name, form }) => {
        return (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <InputNumber
              style={{ width: '45%' }}
              placeholder="请输入"
              min={-999999.99}
              max={999999.99}
              precision={2}
              onChange={v => {
                setState({[name + 'Min']: v})
                form.setFieldsValue({
                  [name + 'Min']: v,
                })
              }}
            />
            <span> -- </span>
            <InputNumber
              style={{ width: '45%' }}
              placeholder="请输入"
              min={state[name + 'Min'] || -999999.99}
              max={999999.99}
              precision={2}
              onChange={v => {
                form.setFieldsValue({
                  [name + 'Max']: v,
                })
              }}
            />
          </div>
        )
      },
      typeRender(v, record){
          return <STags
            style={{fontSize: 12, borderRadius: 3}}
            label={record.alertTypeName}
            fontColor={colorEnums[v]?.fontColor}
            bgColor={colorEnums[v]?.bgColor}/>
      },
      textRender(v, record){
        return <div style={{color: colorEnums[record.alertType]?.textColor, textAlign: 'right'}} >{v}</div>
      },
      costDifferenceRender(v, record){
        return <div style={{color: colorEnums[record.alertType]?.textColor, textAlign: 'right'}} >{v?.toLocaleString?.()}</div>
      },
      exportRender: () => {
        return (
          <Button
            type={'primary'}
            onClick={onExport}
            disabled={state.exportLoading}
            loading={state.exportLoading}
          >
            导出
          </Button>
        )
      },
    }
  }

  return <SDrawerOrModal
    title={'通项标配详情'}
    width={1250}
    open={open}
    onCancel={onClose}
    antdProps={{
      footer: null
    }}
  >
    <SLowcodeList ref={listRef} config={getConfig()} {...extensions}/>
  </SDrawerOrModal>
}

export default GeneralModal